import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $complexes, fetchResidentialComplexesFx } from '../store/complexes';
import { $buildingsByComplex, fetchBuildingsByComplexFx } from '../store/buildings';

import BuildingList from '../components/Buildings/BuildingList';
import ComplexesToolbar from '../components/Complexes/ComplexesToolbar';
import AddBuildingDialog from '../components/Buildings/AddBuildingDialog';
import ReportConfigDialog from '../components/Complexes/ReportConfigDialog';
import ReportSchedulesDialog from '../components/Complexes/ReportSchedulesDialog';
import EditResidentialComplexDialog from '../components/Complexes/EditResidentialComplexDialog';
import SettlementComplexView from '../components/Complexes/SettlementComplexView';
import BuildingArchiveDialog from '../components/Buildings/BuildingArchiveDialog';
import { $canEditComplex, $isSettlementTenant, $objectKind } from '../store';
import {
    Chip,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { reportsApi } from '../api/reports';
import { buildFileName, downloadBlobAsFile } from '../utils/download';
import { showSoftSuccess } from '../utils/softSuccess';
import { complexKindLabel, isSettlementComplex } from '../utils/complexKind';


export default function ComplexDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [openAdd, setOpenAdd] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);

    const [complexes, buildingsMap, pending, canEdit, isSettlementTenant, objectKind] = useUnit([
        $complexes,
        $buildingsByComplex,
        fetchBuildingsByComplexFx.pending,
        $canEditComplex,
        $isSettlementTenant,
        $objectKind,
    ]);

    const complex = useMemo(
        () => (complexes || []).find((c) => String(c.id) === String(id)),
        [complexes, id]
    );

    const settlement = isSettlementTenant || isSettlementComplex(complex);
    const kindLabel = settlement ? 'Посёлок' : complexKindLabel(objectKind);
    const buildings = buildingsMap[String(id)] || [];

    useEffect(() => {
        if (!complexes?.length) {
            fetchResidentialComplexesFx().catch(() => {});
        }
    }, [complexes?.length]);

    useEffect(() => {
        if (id) {
            fetchBuildingsByComplexFx(id).catch(() => {});
        }
    }, [id]);

    const handleExport = useCallback(async () => {
        if (!id) return;
        try {
            setExporting(true);
            const { data, fileName } = await reportsApi.downloadResidentialConsumptionReport({
                residentialComplexId: String(id),
            });
            const fallbackBase = buildFileName(['consumption', String(id)], 'consumption-report');
            const finalName = typeof fileName === 'string' && fileName.trim().length
                ? fileName.trim()
                : `${fallbackBase}.xlsx`;
            downloadBlobAsFile(data, finalName);
        } catch (error) {
            console.error('Не удалось выгрузить отчёт', error);
        } finally {
            setExporting(false);
        }
    }, [id]);

    const handleReportUpdated = useCallback(() => {
        showSoftSuccess('Настройки сохранены');
        fetchResidentialComplexesFx().catch(() => {});
    }, []);

    const openHouse = useCallback((bid) => {
        navigate(`/building/${bid}/corps`);
    }, [navigate]);

    const openArchive = useCallback(() => {
        setArchiveOpen(true);
    }, []);

    return (
        <div
            className="home-container"
            style={{
                width: '100%',
                maxWidth: 'calc(100vw - var(--sidebar-current-width) - 40px)',
                margin: '0 auto',
                paddingLeft: 20,
                paddingRight: 20,
                paddingBottom: 24,
                overflowX: 'hidden',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <button className="rc-btn" onClick={() => navigate(-1)}>← Назад</button>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, noWrap: true }}>
                        {complex?.name || kindLabel}
                    </Typography>
                    <Chip size="small" label={kindLabel} color={settlement ? 'success' : 'default'} variant="outlined" />
                </Stack>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {canEdit && (
                        <Tooltip title={settlement ? 'Редактировать посёлок' : 'Редактировать ЖК'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!complex?.id}
                                    onClick={() => setEditOpen(true)}
                                    aria-label="Редактировать"
                                    sx={{
                                        bgcolor: (theme) =>
                                            theme.palette.mode === "light"
                                                ? "rgba(0,0,0,0.04)"
                                                : "rgba(255,255,255,0.06)",
                                        "&:hover": {
                                            bgcolor: (theme) =>
                                                theme.palette.mode === "light"
                                                    ? "rgba(0,0,0,0.08)"
                                                    : "rgba(255,255,255,0.12)",
                                        },
                                    }}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {canEdit && (
                        <button className="rc-btn" onClick={openArchive}>
                            <DeleteSweepOutlinedIcon sx={{ fontSize: 18, mr: 0.75, verticalAlign: 'text-bottom' }} />
                            Корзина
                        </button>
                    )}
                    <button className="rc-btn" onClick={() => setReportDialogOpen(true)}>Настройки отчета</button>
                    <button className="rc-btn" onClick={() => setScheduleDialogOpen(true)}>Регламентные отчёты</button>
                    {!settlement && (
                        <button className="rc-btn" onClick={() => setOpenAdd(true)}>Добавить дом</button>
                    )}
                </div>
            </div>

            {settlement ? (
                <SettlementComplexView
                    complex={complex}
                    buildings={buildings}
                    loading={pending}
                    canEdit={canEdit}
                    onAddHouse={() => setOpenAdd(true)}
                    onOpenHouse={openHouse}
                    onHouseEdited={async () => {
                        try { await fetchBuildingsByComplexFx(String(id)); } catch {}
                    }}
                />
            ) : (
                <>
                    <ComplexesToolbar
                        viewTab={false}
                        activeTab="Список домов"
                        onTabChange={() => { }}
                        onExport={handleExport}
                        exportLoading={exporting}
                        exportDisabled={!id}
                        onSearch={() => { }}
                        onFilter={() => { }}
                    />

                    <div className="rc-fluid">
                        <BuildingList
                            items={buildings.map((b) => ({
                                id: b.id,
                                address: b.address,
                                name: b.name,
                                counters: {
                                    cold: b.counters?.cold ?? b.device_counts?.hvs ?? 0,
                                    hot: b.counters?.hot ?? b.device_counts?.gvs ?? 0,
                                    heat: b.counters?.heat ?? b.device_counts?.heating ?? 0,
                                    heatAllocators: b.counters?.heatAllocators ?? b.device_counts?.heat_allocators ?? 0,
                                    electricity: b.counters?.electricity ?? b.device_counts?.electricity ?? 0,
                                    all: b.counters?.all ?? b.device_counts?.total ?? b.device_count ?? 0,
                                },
                                incidents: b.incidents ?? { leak: 0, lowBattery: 0, tamper: 0, noReadings: 0 },
                                manualSubmissionEnabled: Boolean(b.manual_submission_enabled),
                            }))}
                            loading={pending}
                            emptyActionLabel={canEdit ? 'Добавить дом' : undefined}
                            onEmptyAction={canEdit ? () => setOpenAdd(true) : undefined}
                            onItemClick={openHouse}
                            onEdited={async () => {
                                try { await fetchBuildingsByComplexFx(String(id)); } catch {}
                            }}
                        />
                    </div>
                </>
            )}
            <ReportConfigDialog
                open={reportDialogOpen}
                onClose={() => setReportDialogOpen(false)}
                complex={complex}
                onUpdated={handleReportUpdated}
            />
            <ReportSchedulesDialog
                open={scheduleDialogOpen}
                onClose={() => setScheduleDialogOpen(false)}
                complex={complex}
                buildings={buildings}
            />
            <EditResidentialComplexDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                complex={complex}
                onSaved={async () => {
                    setEditOpen(false);
                    try {
                        await fetchResidentialComplexesFx();
                    } catch (err) {
                        // ignore refresh errors
                    }
                }}
            />
            <AddBuildingDialog
                open={openAdd}
                onClose={() => setOpenAdd(false)}
                complexId={id}
                settlement={settlement}
                onSuccess={async () => {
                    showSoftSuccess('Дом добавлен');
                    try { await fetchBuildingsByComplexFx(String(id)); } catch { }
                }}
            />
            <BuildingArchiveDialog
                open={archiveOpen}
                onClose={() => setArchiveOpen(false)}
                complexId={id}
                onRestored={async () => {
                    await fetchBuildingsByComplexFx(String(id));
                }}
            />
        </div>
    );
}
